import { createContext, useContext, useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useAuth, isTeenAccount } from './AuthContext'
import { supabase } from '../lib/supabase'

const BROWSER_NOTIF_KEY = 'verse_browser_notif'

function isBrowserNotifEnabled() {
  try {
    const prefs = JSON.parse(localStorage.getItem(BROWSER_NOTIF_KEY) ?? '{}')
    return !!prefs.messages && Notification.permission === 'granted'
  } catch {
    return false
  }
}

const UnreadMessagesContext = createContext(0)
export const useUnreadMessages = () => useContext(UnreadMessagesContext)

// Hoisted from the former useUnreadMessages hook so the poll + Realtime channel
// run ONCE for the whole app (previously instantiated twice — Navbar + BottomNav).
export function UnreadMessagesProvider({ children }) {
  const { user, profile, getToken } = useAuth()
  const [total, setTotal] = useState(0)
  const prevTotalRef = useRef(null)
  const debounceRef  = useRef(null)

  useEffect(() => {
    // PR 2: read teen status from profile (server-authoritative) not user_metadata.
    // F3: also bail while the profile is still loading (user set, profile null) so
    // a teen doesn't fire one stray /api/messages/unread before account_type resolves.
    // The effect re-runs when profile?.accountType lands (dep array), so an adult
    // mid-load starts polling as soon as their profile arrives.
    if (!user || !profile || isTeenAccount(profile)) { setTotal(0); prevTotalRef.current = null; return }

    async function fetchUnread() {
      try {
        const token = await getToken()
        if (!token) return // skip when session not yet populated; next poll will retry
        const { data } = await axios.get('/api/messages/unread', {
          headers: { Authorization: `Bearer ${token}` },
        })
        const next = data.total ?? 0

        // Fire a browser notification if count went up
        if (
          prevTotalRef.current !== null &&
          next > prevTotalRef.current &&
          isBrowserNotifEnabled()
        ) {
          const diff = next - prevTotalRef.current
          new Notification('Verse', {
            body: `You have ${diff} new message${diff === 1 ? '' : 's'}`,
            icon: '/favicon.ico',
          })
        }

        prevTotalRef.current = next
        setTotal(next)
      } catch (err) {
        console.error('[useUnreadMessages] Failed to fetch unread message count:', err)
      }
    }

    fetchUnread()

    // Subscribe to conversation changes via Supabase Realtime
    // RLS ensures we only receive events for our own conversations
    let channel
    try {
      channel = supabase
        .channel('unread-messages')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'conversations',
        }, () => {
          clearTimeout(debounceRef.current)
          debounceRef.current = setTimeout(fetchUnread, 300)
        })
        .subscribe()
    } catch (err) {
      console.warn('[useUnreadMessages] Realtime subscription failed, relying on polling:', err)
    }

    // Fallback poll every 60s in case Realtime disconnects.
    // Visibility-gated (mirrors useNotifications): skip the tick while the tab
    // is backgrounded; resume on the next tick after refocus.
    const fallback = setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnread()
    }, 60000)

    // Same-tab signal: pages can dispatch this to force an immediate refetch
    // (e.g. when a conversation is opened, since realtime can be unreliable)
    function onMessagesRead() { fetchUnread() }
    window.addEventListener('verse:messages-read', onMessagesRead)

    return () => {
      clearTimeout(debounceRef.current)
      clearInterval(fallback)
      if (channel) supabase.removeChannel(channel)
      window.removeEventListener('verse:messages-read', onMessagesRead)
    }
    // PR 2: profile.accountType gates this effect; re-run when it resolves so
    // a teen who lands with profile=null briefly doesn't keep a polling loop.
  }, [user?.id, profile?.accountType]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UnreadMessagesContext.Provider value={total}>
      {children}
    </UnreadMessagesContext.Provider>
  )
}
